import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { ClipboardCheck, ArrowLeft, RotateCcw, Clock, CheckCircle, XCircle, Mail, CreditCard } from 'lucide-react';

export default function RefundPolicy() {
  return (
    <div className="min-h-screen bg-background py-8 px-4">
      <div className="max-w-3xl mx-auto">
        {/* Header */}
        <div className="flex flex-col items-center mb-8">
          <div className="w-16 h-16 bg-primary rounded-xl flex items-center justify-center mb-4">
            <ClipboardCheck className="w-8 h-8 text-white" />
          </div>
          <h1 className="font-heading text-3xl font-bold text-foreground">QA Builder Pro</h1>
          <p className="text-muted-foreground mt-1">Refund Policy</p>
        </div>

        {/* Back Button */}
        <div className="mb-6">
          <Link to="/register">
            <Button variant="ghost" size="sm" data-testid="back-to-register-btn">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Register
            </Button>
          </Link>
        </div>

        {/* Refund Content */}
        <Card className="border-slate-200 shadow-lg">
          <CardHeader>
            <CardTitle className="font-heading text-2xl">Refund Policy</CardTitle>
            <p className="text-sm text-muted-foreground">Last updated: December 2025</p>
          </CardHeader>
          <CardContent className="space-y-8">
            
            {/* Overview */}
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <RotateCcw className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">1. Overview</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                <strong>QA Builder Pro</strong> ("Company", "we", "our") wants you to be completely satisfied with QA Builder Pro. This refund policy outlines the circumstances under which we offer refunds for our subscription plans, in accordance with Paddle's Buyer Terms.
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                <p className="text-green-800 font-medium">
                  We offer a 7-day free trial so you can test all features before purchasing. All paid plans include a 14-day refund guarantee as per Paddle's Buyer Terms.
                </p>
              </div>
            </section>

            {/* Monthly Subscription */}
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <Clock className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">2. Monthly Subscription (£3/month)</h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-foreground">Cancellation</p>
                    <p className="text-muted-foreground text-sm">
                      You can cancel your monthly subscription at any time from your account settings. You will retain access until the end of your current billing period.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-foreground">14-Day Refund Policy</p>
                    <p className="text-muted-foreground text-sm">
                      In accordance with Paddle's Buyer Terms, you may request a full refund within <strong>14 days</strong> of your initial payment, no questions asked.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-foreground">After 14 Days</p>
                    <p className="text-muted-foreground text-sm">
                      Refund requests after 14 days are reviewed on a case-by-case basis. Technical issues that we cannot resolve may qualify for a partial or full refund.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* Lifetime Access */}
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <CreditCard className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">3. Lifetime Access (£10 one-time)</h3>
              </div>
              <div className="space-y-4">
                <div className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-green-600 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-foreground">14-Day Satisfaction Guarantee</p>
                    <p className="text-muted-foreground text-sm">
                      If you're not satisfied with your lifetime purchase, you can request a full refund within <strong>14 days</strong> of payment, no questions asked. This is in accordance with Paddle's Buyer Terms.
                    </p>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <XCircle className="h-5 w-5 text-red-500 mt-0.5 flex-shrink-0" />
                  <div>
                    <p className="font-medium text-foreground">After 14 Days</p>
                    <p className="text-muted-foreground text-sm">
                      Refund requests after 14 days are reviewed on a case-by-case basis. Technical issues that we cannot resolve may qualify for a partial or full refund.
                    </p>
                  </div>
                </div>
              </div>
            </section>

            {/* How to Request */}
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <Mail className="h-5 w-5 text-primary" />
                <h3 className="text-lg font-semibold text-foreground">4. How to Request a Refund</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                To request a refund:
              </p>
              <ol className="list-decimal list-inside text-muted-foreground space-y-2 ml-4">
                <li>Contact us through the application support feature</li>
                <li>Provide your registered email address</li>
                <li>Include your reason for requesting a refund</li>
                <li>We will respond within 2 business days</li>
              </ol>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <p className="text-blue-800 text-sm">
                  <strong>Processing Time:</strong> Approved refunds are processed within 5-10 business days. The refund will be credited to your original payment method.
                </p>
              </div>
            </section>

            {/* Exceptions */}
            <section className="space-y-3">
              <div className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-amber-500" />
                <h3 className="text-lg font-semibold text-foreground">5. Non-Refundable Situations</h3>
              </div>
              <p className="text-muted-foreground leading-relaxed">
                Refunds will generally not be provided in the following situations:
              </p>
              <ul className="list-disc list-inside text-muted-foreground space-y-2 ml-4">
                <li>Request made after the applicable refund period</li>
                <li>Account has been terminated due to violation of our Terms of Service</li>
                <li>You have previously received a refund for QA Builder Pro</li>
                <li>The subscription was purchased through a third-party promotion with special terms</li>
              </ul>
            </section>

            {/* Changes */}
            <section className="space-y-3">
              <h3 className="text-lg font-semibold text-foreground">6. Changes to This Policy</h3>
              <p className="text-muted-foreground leading-relaxed">
                We reserve the right to modify this refund policy at any time. Changes will not affect refund requests already in progress. The current policy at the time of your purchase will apply to your transaction.
              </p>
            </section>

            {/* Contact */}
            <section className="space-y-3">
              <h3 className="text-lg font-semibold text-foreground">7. Questions?</h3>
              <p className="text-muted-foreground leading-relaxed">
                If you have any questions about our refund policy or need assistance, please don't hesitate to contact us through the application. We're here to help!
              </p>
            </section>

            {/* Footer */}
            <div className="border-t pt-6 mt-8">
              <p className="text-sm text-muted-foreground text-center">
                By purchasing QA Builder Pro, you acknowledge that you have read and understood this Refund Policy.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Footer Links */}
        <div className="mt-8 text-center space-x-4">
          <Link to="/register" className="text-primary hover:underline text-sm font-medium">
            Create Account
          </Link>
          <span className="text-muted-foreground">|</span>
          <Link to="/login" className="text-primary hover:underline text-sm font-medium">
            Sign In
          </Link>
          <span className="text-muted-foreground">|</span>
          <Link to="/terms" className="text-primary hover:underline text-sm font-medium">
            Terms
          </Link>
          <span className="text-muted-foreground">|</span>
          <Link to="/privacy" className="text-primary hover:underline text-sm font-medium">
            Privacy
          </Link>
        </div>
      </div>
    </div>
  );
}
